import { formatGridDate, isDateOnlyOnOrBefore, parseDateOnly, startOfTodayLocal, toIsoDateString } from '../../utils/dateUtils';
import { calculateNextDueDate, getMaxGenerateDaysBefore } from '../../utils/frequencyCalculations';
import { normalizeDashboardTableNameForCalendar } from '../../utils/tableNameNormalize';

export function resolveTodoRecordId(record) {
  if (!record || typeof record !== 'object') return '';
  const id = record.id ?? record.Id ?? record.recordId;
  if (id == null || id === '') return '';
  return String(id);
}

export function resolveTodoRecordDueDate(record) {
  if (!record || typeof record !== 'object') return null;
  return record['Due Date']
    || record.DueDate
    || record['Scheduled Date']
    || record.ScheduledDate
    || record.dueDate
    || null;
}

export function extractSavedRecord(saveResult, fallbackRecord) {
  const fromResult = saveResult?.data && typeof saveResult.data === 'object'
    ? saveResult.data
    : (saveResult && typeof saveResult === 'object' ? saveResult : null);
  const merged = { ...(fallbackRecord || {}), ...(fromResult || {}) };
  const id = resolveTodoRecordId(fromResult) || resolveTodoRecordId(fallbackRecord) || resolveTodoRecordId(merged);
  if (!id) return false;
  return { ...merged, id };
}

export function formatLockedLinkedRecordLabel(tableName, recordId) {
  if (!tableName || recordId == null || recordId === '') return '';
  const table = normalizeDashboardTableNameForCalendar(tableName) || tableName;
  return `${String(table).replace(/_/g, ' ')} #${recordId}`;
}

export const TODO_COLOR_PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#f97316'
];

export const TODO_PRIORITIES = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' }
];

export const TODO_FREQUENCY_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Bi-Weekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Bi-Monthly', label: 'Bi-Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annually', label: 'Semi-Annually' },
  { value: 'Annually', label: 'Annually' },
  { value: 'Every x years', label: 'Every x years' }
];

export const emptyTodoFrequency = () => ({
  Repeating: false,
  Frequency: '',
  GenerateDaysBefore: 0,
  FrequencyDayOfWeek: null,
  FrequencyWeekType: null,
  FrequencyDayType: null,
  FrequencyDayNumber: null,
  FrequencyWeekday: null,
  FrequencyMonthType: null,
  FrequencyMonth: null,
  FrequencyYears: null
});

export function todoFrequencyFromRecord(todo) {
  if (!todo) return emptyTodoFrequency();
  return {
    Repeating: Boolean(todo.Repeating),
    Frequency: todo.Frequency || '',
    GenerateDaysBefore: todo.GenerateDaysBefore ?? 0,
    FrequencyDayOfWeek: todo.FrequencyDayOfWeek ?? null,
    FrequencyWeekType: todo.FrequencyWeekType || null,
    FrequencyDayType: todo.FrequencyDayType || null,
    FrequencyDayNumber: todo.FrequencyDayNumber ?? null,
    FrequencyWeekday: todo.FrequencyWeekday ?? null,
    FrequencyMonthType: todo.FrequencyMonthType || null,
    FrequencyMonth: todo.FrequencyMonth ?? null,
    FrequencyYears: todo.FrequencyYears ?? null
  };
}

export function hasRequiredTodoFrequencyOptions(frequency, config = {}) {
  switch (frequency) {
    case 'Daily':
      return true;
    case 'Weekly':
      return config.FrequencyDayOfWeek !== null && config.FrequencyDayOfWeek !== undefined && config.FrequencyDayOfWeek !== '';
    case 'Bi-Weekly':
      return config.FrequencyDayOfWeek !== null && config.FrequencyDayOfWeek !== undefined && config.FrequencyDayOfWeek !== ''
        && Boolean(config.FrequencyWeekType);
    case 'Monthly':
    case 'Bi-Monthly':
    case 'Quarterly':
      return Boolean(config.FrequencyDayType);
    case 'Semi-Annually':
    case 'Annually':
      return Boolean(config.FrequencyDayType) && config.FrequencyMonth != null && config.FrequencyMonth !== '';
    case 'Every x years':
      return Boolean(config.FrequencyDayType)
        && config.FrequencyMonth != null && config.FrequencyMonth !== ''
        && config.FrequencyYears != null && config.FrequencyYears !== '';
    default:
      return false;
  }
}

export function calculateTodoNextDueDate(dueDate, frequency, config = {}) {
  const dueIso = toIsoDateString(dueDate);
  if (!dueIso || !frequency) return null;
  if (!hasRequiredTodoFrequencyOptions(frequency, config)) return null;
  const nextDue = calculateNextDueDate(dueIso, frequency, config);
  if (!nextDue) return null;
  if (nextDue instanceof Date && !Number.isNaN(nextDue.getTime())) {
    const year = nextDue.getFullYear();
    const month = String(nextDue.getMonth() + 1).padStart(2, '0');
    const day = String(nextDue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return toIsoDateString(nextDue);
}

export function calculateTodoNextRun(dueDate, frequency, generateDaysBefore, config = {}) {
  const nextDueIso = calculateTodoNextDueDate(dueDate, frequency, config);
  if (!nextDueIso) return null;
  const parsed = parseDateOnly(nextDueIso);
  if (!parsed) return null;
  const maxDays = getMaxGenerateDaysBefore(frequency, config.FrequencyYears);
  const days = Math.max(0, Math.min(Number(generateDaysBefore) || 0, maxDays >= 0 ? maxDays : 0));
  parsed.setDate(parsed.getDate() - days);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function normalizeTodoColor(value) {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const h = trimmed.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return '';
}

export function todoColorToRgba(hex, alpha) {
  const normalized = normalizeTodoColor(hex);
  if (!normalized) return '';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getTodoColorStyle(color) {
  const hex = normalizeTodoColor(color);
  if (!hex) return null;
  return {
    background: todoColorToRgba(hex, 0.16),
    border: `1px solid ${hex}`,
    textColor: hex,
    hoverBg: todoColorToRgba(hex, 0.28)
  };
}

export function uniqueTodoLabels(todos = []) {
  const seen = new Set();
  const labels = [];
  todos.forEach((todo) => {
    const label = String(todo?.Label || '').trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });
  return labels.sort((a, b) => a.localeCompare(b));
}

export const linkedRecordValue = (tableName, recordId) => {
  if (!tableName || recordId == null || recordId === '') return '';
  return `${tableName}:${recordId}`;
};

export const parseLinkedRecordValue = (value) => {
  const raw = String(value || '');
  const idx = raw.indexOf(':');
  if (idx < 1) return { tableName: null, recordId: null };
  return {
    tableName: raw.slice(0, idx),
    recordId: raw.slice(idx + 1)
  };
};

export function getTodoStatus(todo) {
  if (todo?.Completed) return 'Completed';
  const due = parseDateOnly(todo?.DueDate);
  if (due && due.getTime() < startOfTodayLocal().getTime()) return 'Overdue';
  if (due && isDateOnlyOnOrBefore(due)) return 'Due';
  return 'Open';
}

/** True when the due date is after one calendar month from today. No due date stays visible. */
export function isTodoMoreThanOneMonthOut(todo, today = startOfTodayLocal()) {
  const due = parseDateOnly(todo?.DueDate);
  if (!due || !today) return false;
  const cutoff = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
  return due.getTime() > cutoff.getTime();
}

export function todoMatchesTag(todo, tag) {
  const selected = String(tag || '').trim().toLowerCase();
  if (!selected) return true;
  return String(todo?.Label || '').trim().toLowerCase() === selected;
}

export function getTodoPriorityColor(priority) {
  const value = String(priority || '').toLowerCase();
  if (value === 'high') return 'var(--color-error, #f44336)';
  if (value === 'low') return 'var(--color-text-secondary, #888)';
  return 'var(--color-accent)';
}

export function getTodoStatusColor(status) {
  if (status === 'Completed') return '#4caf50';
  if (status === 'Overdue') return 'var(--color-error, #f44336)';
  if (status === 'Due') return '#b8860b';
  return 'var(--color-text-secondary)';
}

export function formatAssignedRecordOptionLabel(row) {
  if (!row) return '';
  const facility = String(row.facility || '').trim();
  const formLabel = String(row.label || '').trim();
  const desc = String(row.recordDescription || row.recordType || '').trim();
  const dueRaw = formatGridDate(row.dueDate);
  const due = dueRaw && dueRaw !== '-' ? dueRaw : '';
  const parts = [];
  if (facility) parts.push(facility);
  if (due) parts.push(due);
  if (
    desc &&
    desc.toLowerCase() !== facility.toLowerCase() &&
    desc.toLowerCase() !== formLabel.toLowerCase()
  ) {
    parts.push(desc);
  }
  if (parts.length) return parts.join(' — ');
  return row.recordId != null && row.recordId !== '' ? `#${row.recordId}` : '';
}

export function uniquifyAssignedRecordOptionLabels(options = []) {
  const counts = new Map();
  options.forEach((opt) => {
    const key = String(opt.label || '');
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return options.map((opt) => {
    if ((counts.get(String(opt.label || '')) || 0) < 2) return opt;
    return {
      ...opt,
      label: `${opt.label} — #${opt.value}`
    };
  });
}

export function formatTodoLinkedLabel(todo, assignedRecords = []) {
  if (!todo?.LinkedTableName || todo.LinkedRecordId == null || todo.LinkedRecordId === '') {
    return '—';
  }
  const calendarTable = normalizeDashboardTableNameForCalendar(todo.LinkedTableName) || todo.LinkedTableName;
  const match = todo.LinkedRecord || assignedRecords.find(
    (row) =>
      (String(row.tableName) === String(todo.LinkedTableName)
        || String(normalizeDashboardTableNameForCalendar(row.tableName) || row.tableName) === String(calendarTable))
      && String(row.recordId) === String(todo.LinkedRecordId)
  );
  if (match) {
    const pretty = formatAssignedRecordOptionLabel(match);
    if (pretty && pretty !== `#${todo.LinkedRecordId}`) return pretty;
    const formLabel = String(match.label || '').trim();
    if (formLabel) return `${formLabel} #${todo.LinkedRecordId}`;
  }
  const table = String(calendarTable || todo.LinkedTableName).replace(/_/g, ' ');
  return `${table} #${todo.LinkedRecordId}`;
}

export function mapTodosFromResponse(response) {
  const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  return list.map((row) => ({
    ...row,
    id: row.Id ?? row.id
  }));
}

export { formatGridDate };
